import React from 'react'
import { Outlet, Navigate } from 'react-router-dom'
import { motion, type TargetAndTransition } from 'framer-motion'
import { useAuth } from '@/hooks/use-auth-wrapper'

interface CircleConfig {
  size: number
  top: string
  left: string
  gradient: string
  animate: TargetAndTransition
}

const circles: CircleConfig[] = [
  {
    size: 360,
    top: '5%',
    left: '10%',
    gradient: 'from-primary/20 via-secondary/15 to-primary/10',
    animate: {
      x: [0, 35, -25, 0],
      y: [0, -20, 20, 0],
      transition: { duration: 25, repeat: Infinity, repeatType: 'mirror' as const },
    } satisfies TargetAndTransition,
  },
  {
    size: 280,
    top: '35%',
    left: '65%',
    gradient: 'from-secondary/20 via-primary/15 to-secondary/5',
    animate: {
      x: [0, -30, 25, 0],
      y: [0, 25, -15, 0],
      transition: { duration: 30, repeat: Infinity, repeatType: 'mirror' as const },
    } satisfies TargetAndTransition,
  },
  {
    size: 220,
    top: '60%',
    left: '20%',
    gradient: 'from-primary/15 via-accent/20 to-primary/5',
    animate: {
      x: [0, 20, -15, 0],
      y: [0, -25, 15, 0],
      transition: { duration: 28, repeat: Infinity, repeatType: 'mirror' as const },
    } satisfies TargetAndTransition,
  },
  {
    size: 420,
    top: '65%',
    left: '70%',
    gradient: 'from-accent/15 via-primary/10 to-secondary/5',
    animate: {
      x: [0, -20, 15, 0],
      y: [0, 18, -22, 0],
      transition: { duration: 32, repeat: Infinity, repeatType: 'mirror' as const },
    } satisfies TargetAndTransition,
  },
]

export const AuthLayout = React.memo(() => {
  const { isAuthenticated } = useAuth()

  if (isAuthenticated) {
    return <Navigate to="/auth" replace />
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-secondary/5">
      <div className="relative h-full w-full overflow-hidden">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-muted/20 to-primary/10" />
          {circles.map((circle, index) => (
            <motion.span
              key={index}
              className={`absolute rounded-full blur-3xl bg-gradient-to-br ${circle.gradient}`}
              style={{
                width: circle.size,
                height: circle.size,
                top: circle.top,
                left: circle.left,
              }}
              animate={circle.animate}
            />
          ))}
        </div>

        <div className="relative z-10 flex min-h-screen items-center justify-center py-12">
          <div className="w-full max-w-md px-4">
            <Outlet />
          </div>
        </div>
      </div>
    </div>
  )
})

AuthLayout.displayName = 'AuthLayout'
